import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  AttentionMessage,
  Box,
  Grid,
  Heading,
  HStack,
  useThemedValueSwitch,
} from '@tq/ui-components';

import { ExpandableParagraph } from 'components';
import { useIsMobile } from '@tq/ui-components/dist/hooks';
import { Consultation, GetCaseDetailsResponse } from 'types';
import { ApplicationStatusIndicator } from '..';

export interface CaseSummaryProps {
  consultation: Consultation | undefined;
  caseDetails: GetCaseDetailsResponse | undefined;
  isRpc: boolean;
}

export const CaseDetailsSummary: React.FC<CaseSummaryProps> = ({
  consultation,
  caseDetails,
  isRpc,
}) => {
  const { t } = useTranslation('case_summary');
  const { tvs, isTQ } = useThemedValueSwitch();

  const isMobile = useIsMobile();

  const status = caseDetails && caseDetails?.application?.applicationStatus && (
    <ApplicationStatusIndicator
      status={caseDetails.application.applicationStatus}
      separateLabel
    />
  );

  const proposalText = consultation?.summaryDescription ? (
    <>
      <Heading size='md' mb={tvs({ tq: 6 })}>
        {t('proposal_description', 'Proposal description')}
      </Heading>
      <Box
        sx={
          isTQ
            ? {
                bg: 'grey.50',
                p: '1.5rem .75rem',
              }
            : {}
        }
      >
        <ExpandableParagraph
          maxCharacters={{ base: 100, xs: 120, sm: 160, md: 180, lg: 200, xl: 240 }}
        >
          {consultation.summaryDescription}
        </ExpandableParagraph>
      </Box>
    </>
  ) : null;

  const warning = (
    <AttentionMessage
      status='warning'
      title={t('sensitive_content_title')}
      description={t(
        'sensitive_content_warning',
        'Sensitive content - The information below has been provided for the purpose of consultation and should not be shared outside your organisation.'
      )}
    />
  );

  const applicationReference = (
    <Heading size='md'>
      {t('application_reference', 'Application reference')}: {consultation?.reference}
    </Heading>
  );

  return isMobile ? (
    <Grid gridGap={6}>
      <Heading size='lg'>{consultation?.address}</Heading>
      <Heading size='md'>{consultation?.reference}</Heading>
      <Heading size='md'>{caseDetails?.application?.applicationType}</Heading>
      {!isRpc && status}
      {!isRpc && proposalText}
      {warning}
    </Grid>
  ) : (
    <Grid gridGap={tvs({ tq: 9, ni: 6 })}>
      <HStack justifyContent='space-between' alignItems={tvs({ tq: 'flex-start' })}>
        <Heading size='lg' maxW={tvs({ tq: '51rem' })}>
          {consultation?.address}
        </Heading>
        {isRpc ? applicationReference : status}
      </HStack>
      <HStack justifyContent='space-between'>
        <Heading size='md'>{caseDetails?.application?.applicationType}</Heading>
        {!isRpc && applicationReference}
      </HStack>
      {!isRpc && proposalText && <Box>{proposalText}</Box>}
      {warning}
    </Grid>
  );
};
